<template>
  <div
    :class="{
      busy: isBusy || loading,
      'document-metadata-free-text-input': true,
    }"
  >
    <PVInputText
      v-model="value"
      :disabled="disabled || isBusy || loading"
      fluid
      :invalid="!isInputValid"
      @blur="handleSave"
    ></PVInputText>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import {
    UpdateMetadataFunction,
    ValidationFunction,
  } from '../DocumentsMetadata/DocumentsMetadata.types';

  interface DocumentMetadataFreeTextInputProps {
    disabled?: boolean;
    getMetadataFunction: () => Promise<unknown> | unknown;
    initialValue: string;
    metadataField: string;
    validationFunction: ValidationFunction;
    updateMetadataFunction: UpdateMetadataFunction;
    saveInvalid?: boolean;
    loading?: boolean;
  }

  const {
    disabled = false,
    saveInvalid = true,
    ...props
  } = defineProps<DocumentMetadataFreeTextInputProps>();

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const { t } = useI18n();
  const { createToast } = useToast();

  const value = ref('');
  const isBusy = ref(false);
  const isInputValid = computed(() => props.validationFunction(value.value));

  async function handleSave(): Promise<void> {
    if (!isInputValid.value && !saveInvalid) {
      return;
    }
    try {
      isBusy.value = true;
      await props.updateMetadataFunction(props.metadataField, value.value);
      await props.getMetadataFunction();
    } catch {
      createToast(t('errors.error'), t('errors.error'), 'danger');
    } finally {
      isBusy.value = false;
      emit('document-data-changed');
    }
  }

  watch(
    () => props.initialValue,
    (initialValue) => {
      value.value = initialValue;
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .document-metadata-free-text-input {
    display: flex;
    align-items: center;
    column-gap: 1rem;

    button {
      white-space: nowrap;
      background-color: $primary-color;
    }

    &.busy {
      @include pulsating-animation;
    }
  }
</style>
